import type { SkinDocument, SkinModel, SurfaceRef } from "../skin/types";
import { exportSkinPng, importSkinPng } from "../skin/png";

const DATABASE_NAME = "skinpilot";
/** Keep in sync with projects.ts — all stores share one DB. */
const DATABASE_VERSION = 3;
const STORE_NAME = "drafts";
const WORKSPACE_STORE = "workspace";
const PROJECTS_STORE = "projects";
export const DRAFT_KEY = "draft-v1";

export interface DraftMetadata {
  selectedSurface?: SurfaceRef;
  editorMode?: "part" | "part3d" | "uv";
  activeLayer?: "inner" | "outer";
}

export interface DraftSnapshot {
  document: SkinDocument;
  metadata: DraftMetadata;
  updatedAt: number;
}

interface DraftRecord {
  id: typeof DRAFT_KEY;
  png: Blob;
  model: SkinModel;
  metadata: DraftMetadata;
  updatedAt: number;
}

export class DraftPersistenceError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "DraftPersistenceError";
  }
}

export async function saveDraft(
  document: SkinDocument,
  metadata: DraftMetadata = {},
  updatedAt = Date.now(),
): Promise<void> {
  const record: DraftRecord = {
    id: DRAFT_KEY,
    png: await exportSkinPng(document),
    model: document.model,
    metadata: sanitizeMetadata(metadata),
    updatedAt,
  };
  const database = await openDatabase();
  try {
    await requestAsPromise(
      database
        .transaction(STORE_NAME, "readwrite")
        .objectStore(STORE_NAME)
        .put(record),
    );
  } catch (error) {
    throw new DraftPersistenceError("无法保存本地皮肤草稿。", { cause: error });
  } finally {
    database.close();
  }
}

export async function restoreDraft(): Promise<DraftSnapshot | null> {
  const database = await openDatabase();
  let record: DraftRecord | undefined;
  try {
    record = await requestAsPromise<DraftRecord | undefined>(
      database
        .transaction(STORE_NAME, "readonly")
        .objectStore(STORE_NAME)
        .get(DRAFT_KEY),
    );
  } catch (error) {
    throw new DraftPersistenceError("无法读取本地皮肤草稿。", { cause: error });
  } finally {
    database.close();
  }
  if (!record) return null;
  if (
    !(record.png instanceof Blob) ||
    (record.model !== "classic" && record.model !== "slim")
  ) {
    throw new DraftPersistenceError("本地草稿格式无效。");
  }
  return {
    document: await importSkinPng(record.png, record.model),
    metadata: sanitizeMetadata(record.metadata ?? {}),
    updatedAt: record.updatedAt,
  };
}

export async function deleteDraft(): Promise<void> {
  const database = await openDatabase();
  try {
    await requestAsPromise(
      database
        .transaction(STORE_NAME, "readwrite")
        .objectStore(STORE_NAME)
        .delete(DRAFT_KEY),
    );
  } finally {
    database.close();
  }
}

export interface DraftAutosaver {
  schedule(document: SkinDocument, metadata?: DraftMetadata): void;
  flush(): Promise<void>;
  cancel(): void;
}

export interface DraftAutosaverCallbacks {
  onSaved?: () => void;
  onError?: (error: unknown) => void;
}

export function createDraftAutosaver(
  delayMilliseconds = 450,
  callbacks: DraftAutosaverCallbacks = {},
): DraftAutosaver {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let pending: { document: SkinDocument; metadata: DraftMetadata } | undefined;

  const flush = async (): Promise<void> => {
    if (timer) clearTimeout(timer);
    timer = undefined;
    const next = pending;
    pending = undefined;
    if (!next) return;
    try {
      await saveDraft(next.document, next.metadata);
      callbacks.onSaved?.();
    } catch (error) {
      callbacks.onError?.(error);
      throw error;
    }
  };

  return {
    schedule(document, metadata = {}) {
      pending = {
        document: {
          pixels: new Uint8ClampedArray(document.pixels),
          model: document.model,
          size: document.size,
          revision: document.revision,
        },
        metadata: sanitizeMetadata(metadata),
      };
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void flush().catch(() => undefined);
      }, delayMilliseconds);
    },
    flush,
    cancel() {
      if (timer) clearTimeout(timer);
      timer = undefined;
      pending = undefined;
    },
  };
}

function sanitizeMetadata(metadata: DraftMetadata): DraftMetadata {
  const result: DraftMetadata = {};
  if (
    metadata.editorMode === "part" ||
    metadata.editorMode === "part3d" ||
    metadata.editorMode === "uv"
  )
    result.editorMode = metadata.editorMode;
  if (metadata.activeLayer === "inner" || metadata.activeLayer === "outer")
    result.activeLayer = metadata.activeLayer;
  const surface = metadata.selectedSurface;
  if (
    surface &&
    ["head", "body", "rightArm", "leftArm", "rightLeg", "leftLeg"].includes(
      surface.part,
    ) &&
    ["inner", "outer"].includes(surface.layer) &&
    ["front", "back", "left", "right", "top", "bottom"].includes(surface.face)
  ) {
    result.selectedSurface = { ...surface };
  }
  return result;
}

function openDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(
      new DraftPersistenceError("当前浏览器不支持 IndexedDB，无法保存草稿。"),
    );
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME))
        database.createObjectStore(STORE_NAME, { keyPath: "id" });
      if (!database.objectStoreNames.contains(WORKSPACE_STORE))
        database.createObjectStore(WORKSPACE_STORE, { keyPath: "id" });
      if (!database.objectStoreNames.contains(PROJECTS_STORE))
        database.createObjectStore(PROJECTS_STORE, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(
        new DraftPersistenceError("无法打开本地草稿数据库。", {
          cause: request.error,
        }),
      );
    request.onblocked = () =>
      reject(new DraftPersistenceError("本地草稿数据库正被其他页面占用。"));
  });
}

function requestAsPromise<T = IDBValidKey>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
